/**
 * User Menu Component
 * Shows login button when not authenticated, user menu when authenticated
 */
import React, { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Button,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Typography,
  Divider,
  ListItemIcon,
  Box,
  Tooltip
} from '@mui/material';
import {
  Login as LoginIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  Bookmark as BookmarkIcon,
  Notifications as NotificationsIcon,
  LightbulbOutlined as LightbulbIcon,
  ListAlt as ListAltIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

function UserMenu() {
  const { user, isAuthenticated, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);

  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleClose();
    await logout();
    navigate('/');
  };

  const handleNavigate = (path) => {
    handleClose();
    navigate(path);
  };

  // Get initials for avatar
  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Loading state
  if (loading) {
    return null;
  }

  // Not authenticated - show login button
  if (!isAuthenticated) {
    return (
      <Button
        component={RouterLink}
        to="/login"
        color="inherit"
        startIcon={<LoginIcon />}
        sx={{ ml: 1 }}
      >
        Sign In
      </Button>
    );
  }

  // Authenticated - show user menu
  return (
    <>
      <Tooltip title="Account">
        <IconButton
          onClick={handleClick}
          size="small"
          sx={{ ml: 1 }}
          aria-controls={open ? 'user-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
        >
          <Avatar
            sx={{
              width: 32,
              height: 32,
              bgcolor: 'secondary.main',
              fontSize: '0.875rem'
            }}
          >
            {getInitials(user?.display_name)}
          </Avatar>
        </IconButton>
      </Tooltip>

      <Menu
        id="user-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        PaperProps={{
          elevation: 3,
          sx: {
            minWidth: 200,
            overflow: 'visible',
            mt: 1.5,
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {/* User info header */}
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle1" fontWeight="medium">
            {user?.display_name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {user?.email}
          </Typography>
        </Box>

        <Divider sx={{ my: 1 }} />

        {/* Disabled items show a "Coming Soon" indicator so users
            understand the feature is planned rather than broken (#99). */}
        <MenuItem
          onClick={() => handleNavigate('/profile')}
          disabled
        >
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            Profile
            <Typography
              variant="caption"
              sx={{ ml: 1, color: 'text.disabled', fontStyle: 'italic' }}
            >
              Coming Soon
            </Typography>
          </Box>
        </MenuItem>

        <MenuItem
          onClick={() => handleNavigate('/saved-searches')}
        >
          <ListItemIcon>
            <BookmarkIcon fontSize="small" />
          </ListItemIcon>
          Saved Searches
        </MenuItem>

        <MenuItem
          onClick={() => handleNavigate('/alerts')}
        >
          <ListItemIcon>
            <NotificationsIcon fontSize="small" />
          </ListItemIcon>
          Email Alerts
        </MenuItem>

        <MenuItem
          onClick={() => handleNavigate('/feature-request')}
        >
          <ListItemIcon>
            <LightbulbIcon fontSize="small" />
          </ListItemIcon>
          Submit Feature Request
        </MenuItem>

        <MenuItem
          onClick={() => handleNavigate('/my-requests')}
        >
          <ListItemIcon>
            <ListAltIcon fontSize="small" />
          </ListItemIcon>
          My Requests
        </MenuItem>

        <MenuItem
          onClick={() => handleNavigate('/settings')}
        >
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          Settings
        </MenuItem>

        <Divider sx={{ my: 1 }} />

        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          Sign Out
        </MenuItem>
      </Menu>
    </>
  );
}

export default UserMenu;
